import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { Toast } from 'primereact/toast';

// ── Types ─────────────────────────────────────────────────────────────────────

interface School {
  _id?: string;
  id?: string;
  name: string;
  state: string;
  district: string;
  pincode: string;
  school_id_code?: string;
  school_type: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

interface SchoolModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess: () => void;
  /** Pass a school to enter edit mode; omit for create mode */
  school?: School | null;
  toast: React.RefObject<Toast | null>;
}

// ── Static Data ───────────────────────────────────────────────────────────────

const statesAndUTs = [
  { value: 'andhra-pradesh', label: 'Andhra Pradesh' },
  { value: 'arunachal-pradesh', label: 'Arunachal Pradesh' },
  { value: 'assam', label: 'Assam' },
  { value: 'bihar', label: 'Bihar' },
  { value: 'chhattisgarh', label: 'Chhattisgarh' },
  { value: 'goa', label: 'Goa' },
  { value: 'gujarat', label: 'Gujarat' },
  { value: 'haryana', label: 'Haryana' },
  { value: 'himachal-pradesh', label: 'Himachal Pradesh' },
  { value: 'jharkhand', label: 'Jharkhand' },
  { value: 'jammu-and-kashmir', label: 'Jammu and Kashmir' }, // Combined
  { value: 'karnataka', label: 'Karnataka' },
  { value: 'kerala', label: 'Kerala' },
  { value: 'madhya-pradesh', label: 'Madhya Pradesh' },
  { value: 'maharashtra', label: 'Maharashtra' },
  { value: 'manipur', label: 'Manipur' },
  { value: 'meghalaya', label: 'Meghalaya' },
  { value: 'mizoram', label: 'Mizoram' },
  { value: 'nagaland', label: 'Nagaland' },
  { value: 'odisha', label: 'Odisha' },
  { value: 'punjab', label: 'Punjab' },
  { value: 'rajasthan', label: 'Rajasthan' },
  { value: 'sikkim', label: 'Sikkim' },
  { value: 'tamil-nadu', label: 'Tamil Nadu' },
  { value: 'telangana', label: 'Telangana' },
  { value: 'tripura', label: 'Tripura' },
  { value: 'uttar-pradesh', label: 'Uttar Pradesh' },
  { value: 'uttarakhand', label: 'Uttarakhand' },
  { value: 'west-bengal', label: 'West Bengal' },
  { value: 'andaman-nicobar', label: 'Andaman and Nicobar Islands' },
  { value: 'chandigarh', label: 'Chandigarh' },
  { value: 'dadra-nagar-haveli', label: 'Dadra and Nagar Haveli and Daman and Diu' },
  { value: 'delhi', label: 'Delhi' },
  { value: 'ladakh', label: 'Ladakh' },
  { value: 'lakshadweep', label: 'Lakshadweep' },
  { value: 'puducherry', label: 'Puducherry' },
];

const districtsByState: Record<string, { value: string; label: string }[]> = {
  'jammu-and-kashmir': [
    // Jammu Division
    { value: 'jammu', label: 'Jammu' },
    { value: 'kathua', label: 'Kathua' },
    { value: 'doda', label: 'Doda' },
    { value: 'rajouri', label: 'Rajouri' },
    { value: 'poonch', label: 'Poonch' },
    { value: 'udhampur', label: 'Udhampur' },
    { value: 'reasi', label: 'Reasi' },
    { value: 'ramban', label: 'Ramban' },
    { value: 'kishtwar', label: 'Kishtwar' },
    { value: 'samba', label: 'Samba' },
    // Kashmir Division
    { value: 'srinagar', label: 'Srinagar' },
    { value: 'anantnag', label: 'Anantnag' },
    { value: 'baramulla', label: 'Baramulla' },
    { value: 'budgam', label: 'Budgam' },
    { value: 'kupwara', label: 'Kupwara' },
    { value: 'pulwama', label: 'Pulwama' },
    { value: 'ganderbal', label: 'Ganderbal' },
    { value: 'bandipora', label: 'Bandipora' },
    { value: 'kulgam', label: 'Kulgam' },
    { value: 'shopian', label: 'Shopian' },
  ],
  maharashtra: [
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'pune', label: 'Pune' },
    { value: 'nagpur', label: 'Nagpur' },
    { value: 'nashik', label: 'Nashik' },
    { value: 'thane', label: 'Thane' },
  ],
  delhi: [
    { value: 'central-delhi', label: 'Central Delhi' },
    { value: 'east-delhi', label: 'East Delhi' },
    { value: 'new-delhi', label: 'New Delhi' },
    { value: 'north-delhi', label: 'North Delhi' },
    { value: 'south-delhi', label: 'South Delhi' },
  ],
  karnataka: [
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'mysore', label: 'Mysore' },
    { value: 'mangalore', label: 'Mangalore' },
    { value: 'hubli', label: 'Hubli' },
  ],
  punjab: [
    { value: 'amritsar', label: 'Amritsar' },
    { value: 'ludhiana', label: 'Ludhiana' },
    { value: 'jalandhar', label: 'Jalandhar' },
    { value: 'patiala', label: 'Patiala' },
    { value: 'mohali', label: 'Mohali' },
  ],
};

const schoolTypeOptions = [
  { key: 'private',       label: 'Private',       icon: 'pi-briefcase',    color: '#3b82f6' },
  { key: 'public',        label: 'Public',         icon: 'pi-check-circle', color: '#10b981' },
  { key: 'international', label: 'International',  icon: 'pi-globe',        color: '#f59e0b' },
];

const statusOptions = [
  { value: 'active',    label: 'Active',    color: '#16a34a' },
  { value: 'inactive',  label: 'Inactive',  color: '#991b1b' },
  { value: 'suspended', label: 'Suspended', color: '#d97706' },
  { value: 'pending',   label: 'Pending',   color: '#1e40af' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const toSlug = (value: string): string =>
  value.toLowerCase().trim().replace(/\s+/g, '-');

const getSchoolId = (school: School): string =>
  school._id || school.id || '';

// ── Sub-components ────────────────────────────────────────────────────────────

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label style={{ display: 'block', marginBottom: '7px', color: '#374151', fontWeight: '600', fontSize: '13px', letterSpacing: '0.01em' }}>
    {children}{required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
  </label>
);

const TextInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    style={{
      width: '100%',
      padding: '10px 13px',
      border: '1.5px solid #e5e7eb',
      borderRadius: '9px',
      fontSize: '14px',
      color: '#111827',
      background: '#fff',
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 0.18s',
      ...props.style,
    }}
    onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; props.onFocus?.(e); }}
    onBlur={(e)  => { e.currentTarget.style.borderColor = '#e5e7eb'; props.onBlur?.(e); }}
  />
);

const SelectInput: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select
    {...props}
    style={{
      width: '100%',
      padding: '10px 13px',
      border: '1.5px solid #e5e7eb',
      borderRadius: '9px',
      fontSize: '14px',
      color: '#111827',
      background: '#fff',
      outline: 'none',
      cursor: 'pointer',
      transition: 'border-color 0.18s',
      ...props.style,
    }}
    onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
    onBlur={(e)  => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
  />
);

// ── Main Component ─────────────────────────────────────────────────────────────

export default function SchoolModal({ visible, onHide, onSuccess, school, toast }: SchoolModalProps) {
  const isEdit = Boolean(school && getSchoolId(school));

  const [formData, setFormData] = useState({
    name: '',
    state: '',
    district: '',
    pincode: '',
    schoolType: '',
    status: 'active',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setError(null);
      if (school) {
        setFormData({
          name:       school.name        || '',
          state:      toSlug(school.state    || ''),
          district:   toSlug(school.district || ''),
          pincode:    school.pincode     || '',
          schoolType: school.school_type?.toLowerCase() || '',
          status:     school.status?.toLowerCase() || 'active',
        });
      } else {
        setFormData({
          name: '',
          state: '',
          district: '',
          pincode: '',
          schoolType: '',
          status: 'active',
        });
      }
    }
  }, [visible, school]);

  const availableDistricts = formData.state ? districtsByState[formData.state] || [] : [];
  const districtNotInList  = formData.district && availableDistricts.length > 0 &&
    !availableDistricts.find((d) => d.value === formData.district);

  const handleStateChange = (value: string) =>
    setFormData((p) => ({ ...p, state: value, district: '' }));

  const handleSave = async () => {
    const { name, state, district, pincode, schoolType, status } = formData;

    if (!name || !state || !district || !pincode || !schoolType) {
      setError('Please fill all required fields.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        name,
        state,
        district,
        pincode,
        school_type: schoolType,
        status,
      };

      let response;
      if (isEdit) {
        const schoolId = getSchoolId(school!);
        response = await apiClient.put(`/api/schools/${schoolId}`, payload);
      } else {
        response = await apiClient.post('/api/schools', payload);
      }

      const data = response.data;
      if (data.school || data.message) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: isEdit
            ? 'School updated successfully!'
            : `School created! Code: ${data.school?.school_id_code || ''}`,
          life: 4000,
        });
        onSuccess();
      } else {
        setError(data.error || data.message || 'Failed to save school.');
      }
    } catch (err: any) {
      const status = err.response?.status;
      const body = err.response?.data;

      if (status === 401) setError('Authentication failed. Please login again.');
      else if (status === 403) setError('You do not have permission to perform this action.');
      else if (status === 409) setError(body?.error || 'A school with this name already exists.');
      else if (status === 400) setError(body?.error || body?.details || 'Invalid data provided.');
      else setError(body?.error || err.message || 'An error occurred while saving.');
    } finally {
      setLoading(false);
    }
  };

  const modalHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        background: 'rgba(255,255,255,0.2)',
        borderRadius: '10px', padding: '8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className={`pi ${isEdit ? 'pi-pencil' : 'pi-plus'}`} style={{ fontSize: '18px', color: 'white' }} />
      </div>
      <div>
        <div style={{ color: 'white', fontWeight: '700', fontSize: '17px', lineHeight: 1.2 }}>
          {isEdit ? 'Edit School' : 'Add New School'}
        </div>
        {isEdit && (
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '2px' }}>
            {school?.name}
            {school?.school_id_code && (
              <span style={{
                marginLeft: '8px',
                fontFamily: 'monospace',
                background: 'rgba(255,255,255,0.15)',
                padding: '1px 8px',
                borderRadius: '6px',
                fontSize: '11px',
              }}>
                {school.school_id_code}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .smt-input:focus { border-color: #3b82f6 !important; }
        .school-type-radio:checked + span { background: #eff6ff; border-color: #3b82f6; }
        .p-dialog.school-unified-modal .p-dialog-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #2855ae 60%, #3b82f6 100%) !important;
          color: white !important;
          border-radius: 16px 16px 0 0 !important;
          padding: 18px 24px !important;
        }
        .p-dialog.school-unified-modal .p-dialog-header .p-dialog-header-icon {
          color: rgba(255,255,255,0.7) !important;
        }
        .p-dialog.school-unified-modal .p-dialog-header .p-dialog-header-icon:hover {
          color: white !important;
          background: rgba(255,255,255,0.15) !important;
        }
        .p-dialog.school-unified-modal .p-dialog-content {
          padding: 0 !important;
          border-radius: 0 0 16px 16px !important;
          overflow: hidden;
        }
        .p-dialog.school-unified-modal {
          border-radius: 16px !important;
          box-shadow: 0 24px 64px rgba(0,0,0,0.18) !important;
        }
      `}</style>

      <Dialog
        header={modalHeader}
        visible={visible}
        onHide={onHide}
        style={{ width: '680px', maxWidth: '94vw' }}
        draggable={false}
        resizable={false}
        className="school-unified-modal"
        footer={null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ padding: '24px', overflowY: 'auto', maxHeight: '72vh' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              <div>
                <FieldLabel required>School Name</FieldLabel>
                <TextInput
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Enter school name"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <FieldLabel required>State / UT</FieldLabel>
                  <SelectInput
                    value={formData.state}
                    onChange={(e) => handleStateChange(e.target.value)}
                  >
                    <option value="">Select State / UT</option>
                    {statesAndUTs.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </SelectInput>
                </div>

                <div>
                  <FieldLabel required>District</FieldLabel>
                  {availableDistricts.length === 0 ? (
                    <TextInput
                      type="text"
                      value={formData.district}
                      onChange={(e) => setFormData((p) => ({ ...p, district: e.target.value }))}
                      placeholder="Enter district"
                    />
                  ) : (
                    <SelectInput
                      value={formData.district}
                      onChange={(e) => setFormData((p) => ({ ...p, district: e.target.value }))}
                    >
                      <option value="">Select District</option>
                      {districtNotInList && (
                        <option value={formData.district}>{formData.district} (current)</option>
                      )}
                      {availableDistricts.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </SelectInput>
                  )}
                </div>
              </div>

              <div>
                <FieldLabel required>Pincode</FieldLabel>
                <TextInput
                  type="text"
                  value={formData.pincode}
                  onChange={(e) => setFormData((p) => ({ ...p, pincode: e.target.value }))}
                  placeholder="6-digit pincode"
                  maxLength={6}
                />
              </div>

              <div>
                <FieldLabel required>School Type</FieldLabel>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {schoolTypeOptions.map((opt) => {
                    const isActive = formData.schoolType === opt.key;
                    return (
                      <label
                        key={opt.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 18px',
                          background: isActive ? '#eff6ff' : '#f9fafb',
                          borderRadius: '10px',
                          border: `2px solid ${isActive ? '#3b82f6' : '#e5e7eb'}`,
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: isActive ? '#1e40af' : '#6b7280',
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="radio"
                          value={opt.key}
                          checked={isActive}
                          onChange={(e) => setFormData((p) => ({ ...p, schoolType: e.target.value }))}
                          style={{ accentColor: opt.color, cursor: 'pointer' }}
                        />
                        <i className={`pi ${opt.icon}`} style={{ fontSize: '13px', color: isActive ? opt.color : '#9ca3af' }} />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {isEdit && (
                <div>
                  <FieldLabel>Status</FieldLabel>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {statusOptions.map((opt) => {
                      const isActive = formData.status === opt.value;
                      return (
                        <label
                          key={opt.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: isActive ? `${opt.color}12` : '#f9fafb',
                            borderRadius: '10px',
                            border: `2px solid ${isActive ? opt.color : '#e5e7eb'}`,
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: isActive ? opt.color : '#6b7280',
                            transition: 'all 0.15s',
                          }}
                        >
                          <input
                            type="radio"
                            value={opt.value}
                            checked={isActive}
                            onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                            style={{ accentColor: opt.color, cursor: 'pointer' }}
                          />
                          {opt.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {error && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: '9px', padding: '11px 14px',
                  color: '#dc2626', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <i className="pi pi-exclamation-circle" />
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
                <button
                  onClick={onHide}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '12px',
                    background: '#f3f4f6', border: '1.5px solid #e5e7eb',
                    borderRadius: '10px', cursor: 'pointer',
                    color: '#374151', fontSize: '14px', fontWeight: '600',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e5e7eb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    flex: 2, padding: '12px',
                    background: loading ? '#93c5fd' : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    border: 'none', borderRadius: '10px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    color: 'white', fontSize: '14px', fontWeight: '600',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    boxShadow: loading ? 'none' : '0 4px 14px rgba(59,130,246,0.35)',
                    transition: 'all 0.15s',
                  }}
                >
                  {loading ? (
                    <><i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }} /> Saving...</>
                  ) : isEdit ? (
                    <><i className="pi pi-check" style={{ fontSize: '14px' }} /> Update School</>
                  ) : (
                    <><i className="pi pi-plus" style={{ fontSize: '14px' }} /> Create School</>
                  )}
                </button>
              </div>

              {!isEdit && (
                <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', margin: 0 }}>
                  A unique school code will be auto-generated upon creation.
                </p>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}